import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import Button from 'components/Button'
import {
	Table,
	TableHeader,
	TableBody,
	TableHead,
	TableRow,
	TableCell,
} from 'components/ui/table'
import { XCircle, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'
import moment from 'moment'

const headerCells = [
	{ id: 'templateName', label: 'Template Name', sortable: true },
	{ id: 'createdBy.name', label: 'Created By', sortable: true },
	{ id: 'templateType', label: 'Template Type', sortable: true },
	{ id: 'createdAt', label: 'Created', sortable: true },
	{ id: 'updatedAt', label: 'Updated', sortable: true },
	{ id: 'action', label: '', sortable: false },
]

const TemplatesTable = ({ templates, handleDeleteDetail }) => {
	const [orderBy, setOrderBy] = useState(null)
	const [order, setOrder] = useState('asc')

	const handleSort = columnId => {
		if (orderBy === columnId) {
			setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(columnId)
			setOrder('asc')
		}
	}

	const getNestedValue = (obj, path) => {
		return path.split('.').reduce((acc, key) => acc?.[key], obj)
	}

	const sortedRecords = useMemo(() => {
		if (!orderBy) return templates
		return [...templates].sort((a, b) => {
			const aVal = (getNestedValue(a, orderBy) || '').toString().toLowerCase()
			const bVal = (getNestedValue(b, orderBy) || '').toString().toLowerCase()
			if (aVal < bVal) return order === 'asc' ? -1 : 1
			if (aVal > bVal) return order === 'asc' ? 1 : -1
			return 0
		})
	}, [templates, orderBy, order])

	const SortIcon = ({ columnId }) => {
		if (orderBy !== columnId) return <ArrowUpDown className="ml-1 h-3 w-3 text-gray-300" />
		return order === 'asc' ? (
			<ArrowUp className="ml-1 h-3 w-3 text-primary" />
		) : (
			<ArrowDown className="ml-1 h-3 w-3 text-primary" />
		)
	}

	return (
		<div>
			<Table>
				<TableHeader>
					<TableRow>
						{headerCells.map(cell => (
							<TableHead
								key={cell.id}
								className={cell.sortable ? 'cursor-pointer select-none' : ''}
								onClick={() => cell.sortable && handleSort(cell.id)}
							>
								<div className="flex items-center">
									{cell.label}
									{cell.sortable && <SortIcon columnId={cell.id} />}
								</div>
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{sortedRecords.map(template => (
						<TableRow key={template._id} className="h-7 hover:bg-[#fffbf2] cursor-pointer group">
							<TableCell className="py-1 text-sm">
								<Link to={`/templates/${template._id}/${template.templateType}`}>
									{template.templateName}
								</Link>
							</TableCell>
							<TableCell className="py-1 text-sm">
								<div>{template.createdBy.name}</div>
							</TableCell>
							<TableCell className="py-1 text-sm">
								<div>
									{template.templateType === `task` ? 'Task Template' : 'Site Detail Template'}
								</div>
							</TableCell>
							<TableCell className="py-1 text-sm">
								<div>{moment(template.createdAt).format('MM/DD/YYYY')}</div>
							</TableCell>
							<TableCell className="py-1 text-sm">
								<div>{moment(template.updatedAt).format('MM/DD/YYYY')}</div>
							</TableCell>
							<TableCell className="py-1 text-sm text-right">
								<div className="flex justify-end">
									<Button
										onClick={() => handleDeleteDetail(template)}
										variant="ghost-red"
										isOutline
										aria-label="Delete"
										className="opacity-0 group-hover:opacity-100 transition-opacity m-0 p-0 shadow-none"
									>
										<XCircle className="w-4 h-4" />
									</Button>
								</div>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	)
}

export default TemplatesTable
